import { useEffect, useState } from "react";
import type { DersdinleUser as User } from "@/lib/api";
import { getDataClient } from "@/lib/dataClient";
import type { CoachRole } from "@/mocks/aiCoach";

export const VALID_ROLES: CoachRole[] = ["ogrenci", "veli", "ogretmen", "admin"];

// user_metadata içindeki role alanından güvenli rolü çözümler.
// Geçersiz/eksik değerler "guest" olarak değerlendirilir.
function resolveRole(user: User | null): CoachRole {
  if (!user) return "guest";
  const raw = user.user_metadata?.role as unknown;
  if (typeof raw === "string" && (VALID_ROLES as string[]).includes(raw)) {
    return raw as CoachRole;
  }
  return "guest";
}

// Bir role ait ana panel yolunu döndürür.
export function roleHomePath(role: CoachRole): string {
  switch (role) {
    case "ogretmen":
      return "/ogretmen";
    case "veli":
      return "/veli";
    case "admin":
      return "/yonetici";
    case "ogrenci":
      return "/ogrenci";
    default:
      return "/giris";
  }
}

// Bir role ait özel giriş sayfasının yolunu döndürür.
export function roleLoginPath(role: CoachRole): string {
  switch (role) {
    case "ogretmen":
      return "/ogretmen/giris";
    case "admin":
      return "/yonetici/giris";
    default:
      return "/giris";
  }
}

// dataClient auth hata mesajlarını kullanıcı dostu Türkçeye çevirir.
export function translateAuthError(message: string): string {
  const safeMessage = String(message || "").trim();
  const m = safeMessage.toLowerCase();
  if (m.includes("invalid login credentials") || m.includes("e-posta veya parola hatalı") || m.includes("e-posta veya şifre hatalı")) {
    return "E-posta veya şifre hatalı. Lütfen bilgilerinizi kontrol edin.";
  }
  if (m.includes("email not confirmed")) {
    return "E-posta adresiniz henüz doğrulanmamış. Gelen kutunuzdaki doğrulama bağlantısına tıklayın.";
  }
  if (m.includes("user already registered")) {
    return "Bu e-posta adresiyle zaten bir hesap oluşturulmuş.";
  }
  if (m.includes("zaten kayıtlı") || m.includes("zaten kayitli")) {
    return "Bu e-posta adresiyle zaten bir hesap oluşturulmuş. Giriş yapmayı deneyin.";
  }
  if (m.includes("password should be at least")) {
    return "Şifreniz en az 8 karakter olmalı.";
  }
  if (m.includes("rate limit")) {
    return "Çok fazla deneme yaptınız. Lütfen biraz bekleyip tekrar deneyin.";
  }
  if (m.includes("invalid email") || m.includes("email address is invalid") || m.includes("geçerli bir e-posta")) {
    return "Geçerli bir e-posta adresi girin.";
  }
  if (m.includes("ad soyad") || m.includes("üyelik türü") || m.includes("üyelik turu")) {
    return "Ad soyad, üyelik türü ve gerekli profil bilgilerini eksiksiz doldurun.";
  }
  if (m.includes("şifreniz en az") || m.includes("parolanız en az")) {
    return "Şifreniz en az 8 karakter olmalıdır.";
  }
  if (m.includes("sunucu hatası") || m.includes("sunucu hatasi")) {
    return "Kayıt altyapısında geçici bir sorun oluştu. Lütfen birkaç saniye sonra yeniden deneyin.";
  }
  // Dersdinle API'si yalnızca kullanıcıya gösterilebilecek denetimli hata
  // mesajları üretir; bunları kayıp bilgi yerine doğrudan göster.
  if (safeMessage && safeMessage !== "Kayıt oluşturulamadı.") {
    return safeMessage;
  }
  return "Giriş işlemi sırasında bir hata oluştu. Lütfen tekrar deneyin.";
}

export interface AuthInfo {
  user: User | null;
  role: CoachRole;
  loading: boolean;
}

/**
 * Mevcut oturumu okur ve kullanıcının rolünü döndürür.
 * Oturum değişikliklerini (giriş/çıkış) canlı olarak takip eder.
 */
export function useAuth(): AuthInfo {
  const [state, setState] = useState<AuthInfo>({
    user: null,
    role: "guest",
    loading: true,
  });

  useEffect(() => {
    const dataClient = getDataClient();

    // İlk oturumu oku.
    dataClient.auth.getSession().then(({ data }) => {
      const user = data.session?.user ?? null;
      setState({ user, role: resolveRole(user), loading: false });
    });

    // Oturum değişikliklerini dinle (senkron, await yok).
    const { data: sub } = dataClient.auth.onAuthStateChange((_event, session) => {
      const user = session?.user ?? null;
      setState({ user, role: resolveRole(user), loading: false });
    });

    return () => {
      sub.subscription.unsubscribe();
    };
  }, []);

  return state;
}
